import { useState, useRef, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import ArtworkSlideshow from './ArtworkSlideshow';

interface EntranceHallProps {
  onEnter: () => void;
  onContact: () => void;
}

const STORAGE_KEY = 'entrance_hall_state';
const AUDIO_URL_KEY = 'entrance_hall_audio_url';

// Only show editing tools on the Readdy editor, not on the live site
const isEditMode = typeof window !== 'undefined' &&
  (window.location.hostname.includes('readdy.ai') ||
   window.location.hostname === 'localhost' ||
   window.location.hostname === '127.0.0.1');

interface SavedState {
  artworkSrc: string;
  size: number;
  offsetX: number;
  offsetY: number;
}

interface SlideshowArtwork {
  id: number;
  imageUrl: string;
  title: string;
}

const FALLBACK_BG =
  'https://public.readdy.ai/ai/img_res/5c90c04234ed3f6e54952e69e9f8fa4e.jpg';

export default function EntranceHall({ onEnter, onContact }: EntranceHallProps) {
  const [isEntering, setIsEntering] = useState(false);
  const [artworkSrc, setArtworkSrc] = useState(
    'https://csfkoahootbkqsubfcim.supabase.co/storage/v1/object/public/artworks/1772567689315-mh8s3jg0huq.jpeg'
  );
  const [isSelected, setIsSelected] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);
  const [size, setSize] = useState(100);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved'>('idle');
  const [slideshowArtworks, setSlideshowArtworks] = useState<SlideshowArtwork[]>([]);

  // Audio state
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);
  const [audioUploadStatus, setAudioUploadStatus] = useState<'idle' | 'uploading' | 'done' | 'error'>('idle');
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioFileInputRef = useRef<HTMLInputElement>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const artworkRef = useRef<HTMLDivElement>(null);

  // Fetch all uploaded artworks for the slideshow
  useEffect(() => {
    const fetchSlides = async () => {
      const { data, error } = await supabase
        .from('artworks')
        .select('id, title, image_url')
        .order('sort_order', { ascending: true });

      if (!error && data && data.length > 0) {
        setSlideshowArtworks(
          data.map((row: any) => ({
            id: row.id,
            title: row.title ?? '',
            imageUrl: row.image_url,
          }))
        );
      }
    };
    fetchSlides();
  }, []);

  // Load saved artwork state
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved: SavedState = JSON.parse(raw);
        if (saved.artworkSrc) setArtworkSrc(saved.artworkSrc);
        if (saved.size !== undefined) setSize(saved.size);
        if (saved.offsetX !== undefined) setOffsetX(saved.offsetX);
        if (saved.offsetY !== undefined) setOffsetY(saved.offsetY);
      }
    } catch {
      // ignore
    }
  }, []);

  // Load saved audio URL from localStorage
  useEffect(() => {
    const savedAudio = localStorage.getItem(AUDIO_URL_KEY);
    if (savedAudio) {
      setAudioUrl(savedAudio);
    } else {
      // Check Supabase storage for existing audio
      const fetchAudio = async () => {
        const { data } = await supabase.storage.from('audio').list('', { limit: 1, sortBy: { column: 'created_at', order: 'desc' } });
        if (data && data.length > 0) {
          const { data: urlData } = supabase.storage.from('audio').getPublicUrl(data[0].name);
          if (urlData?.publicUrl) {
            setAudioUrl(urlData.publicUrl);
            localStorage.setItem(AUDIO_URL_KEY, urlData.publicUrl);
          }
        }
      };
      fetchAudio();
    }
  }, []);

  // Auto-play audio (muted by default — browser policy)
  useEffect(() => {
    if (audioRef.current && audioUrl) {
      audioRef.current.volume = 0.6;
      audioRef.current.muted = true;
      audioRef.current.loop = false; // We handle looping manually for fade
      audioRef.current.play().catch(() => {});
    }
  }, [audioUrl]);

  // Fade out last 5 seconds, then loop seamlessly
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const FADE_DURATION = 5; // seconds
    const BASE_VOLUME = 0.6;
    let fadeInterval: ReturnType<typeof setInterval> | null = null;

    const handleTimeUpdate = () => {
      if (!audio.duration || isMuted) return;
      const remaining = audio.duration - audio.currentTime;

      if (remaining <= FADE_DURATION) {
        // Fade out
        const fraction = remaining / FADE_DURATION;
        audio.volume = Math.max(0, BASE_VOLUME * fraction);
      } else {
        // Restore volume if not in fade zone
        if (audio.volume !== BASE_VOLUME) {
          audio.volume = BASE_VOLUME;
        }
      }
    };

    const handleEnded = () => {
      audio.volume = BASE_VOLUME;
      audio.currentTime = 0;
      audio.play().catch(() => {});
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      if (fadeInterval) clearInterval(fadeInterval);
    };
  }, [audioUrl, isMuted]);

  // Sync mute state
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isMuted;
      if (!isMuted) {
        audioRef.current.volume = 0.6;
        audioRef.current.play().catch(() => {});
      }
    }
  }, [isMuted]);

  useEffect(() => {
    if (!isSelected || !isEditMode) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (artworkRef.current && !artworkRef.current.contains(e.target as Node)) {
        setIsSelected(false);
        setShowAdjust(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSelected]);

  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    const state: SavedState = { artworkSrc, size, offsetX, offsetY };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    setSaveStatus('saved');
    setTimeout(() => setSaveStatus('idle'), 2000);
  };

  const handleEnter = () => {
    setIsEntering(true);
    setTimeout(() => onEnter(), 800);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        if (dataUrl) {
          setArtworkSrc(dataUrl);
          const currentState: SavedState = { artworkSrc: dataUrl, size, offsetX, offsetY };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(currentState));
        }
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const handleAudioUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingAudio(true);
    setAudioUploadStatus('uploading');

    const fileName = `entrance-audio-${Date.now()}.${file.name.split('.').pop()}`;
    const { error } = await supabase.storage.from('audio').upload(fileName, file, { upsert: true, contentType: file.type });

    if (error) {
      setAudioUploadStatus('error');
      setIsUploadingAudio(false);
      setTimeout(() => setAudioUploadStatus('idle'), 3000);
      return;
    }

    const { data: urlData } = supabase.storage.from('audio').getPublicUrl(fileName);
    if (urlData?.publicUrl) {
      setAudioUrl(urlData.publicUrl);
      localStorage.setItem(AUDIO_URL_KEY, urlData.publicUrl);
      setAudioUploadStatus('done');
      setIsMuted(false);
    }
    setIsUploadingAudio(false);
    setTimeout(() => setAudioUploadStatus('idle'), 3000);
    e.target.value = '';
  };

  const handleArtworkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isEditMode) setIsSelected(true);
  };

  const toggleMute = () => setIsMuted(v => !v);

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black">
      {/* Audio element */}
      {audioUrl && (
        <audio ref={audioRef} src={audioUrl} preload="auto" />
      )}

      {/* Full-frame artwork slideshow background */}
      <ArtworkSlideshow artworks={slideshowArtworks} fallbackSrc={FALLBACK_BG} />

      {/* Painting on the wall */}
      <div
        className="absolute inset-0 flex items-center justify-center z-10"
        style={{ paddingBottom: '8rem' }}
      >
        <div className="flex flex-col items-center">
          <div
            ref={artworkRef}
            className={`relative select-none ${isEditMode ? 'cursor-pointer' : ''}`}
            style={{
              transform: `translate(${offsetX}px, ${offsetY}px) scale(${size / 100})`,
              transformOrigin: 'center center',
              transition: 'transform 0.1s ease',
            }}
            onClick={handleArtworkClick}
          >
            {isEditMode && !isSelected && (
              <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-200 bg-black/10">
                <div className="bg-black/60 text-white text-xs tracking-widest uppercase px-3 py-1.5 rounded font-light" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Click to edit
                </div>
              </div>
            )}

            {isEditMode && isSelected && (
              <div
                className="absolute -top-12 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-black/80 backdrop-blur-sm rounded-full px-2 py-1.5 shadow-xl"
                onClick={e => e.stopPropagation()}
                style={{ fontFamily: "'Inter', sans-serif", whiteSpace: 'nowrap' }}
              >
                <button
                  type="button"
                  onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}
                  className="flex items-center gap-1.5 px-3 py-1 text-white text-xs tracking-widest uppercase font-light hover:bg-white/10 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-image-edit-line text-xs"></i>
                  </div>
                  Change
                </button>
                <div className="w-px h-4 bg-white/20"></div>
                <button
                  type="button"
                  onClick={e => { e.stopPropagation(); setShowAdjust(v => !v); }}
                  className={`flex items-center gap-1.5 px-3 py-1 text-xs tracking-widest uppercase font-light rounded-full transition-colors cursor-pointer whitespace-nowrap ${showAdjust ? 'bg-white/20 text-white' : 'text-white hover:bg-white/10'}`}
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-settings-3-line text-xs"></i>
                  </div>
                  Adjust
                </button>
                <div className="w-px h-4 bg-white/20"></div>
                <button
                  type="button"
                  onClick={handleSave}
                  className={`flex items-center gap-1.5 px-3 py-1 text-xs tracking-widest uppercase font-light rounded-full transition-colors cursor-pointer whitespace-nowrap ${saveStatus === 'saved' ? 'text-green-400' : 'text-white hover:bg-white/10'}`}
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className={`text-xs ${saveStatus === 'saved' ? 'ri-check-line' : 'ri-save-line'}`}></i>
                  </div>
                  {saveStatus === 'saved' ? 'Saved!' : 'Save'}
                </button>
              </div>
            )}

            {isEditMode && isSelected && showAdjust && (
              <div
                className="absolute -bottom-44 left-1/2 -translate-x-1/2 bg-black/80 backdrop-blur-sm rounded-xl px-5 py-4 flex flex-col gap-3 shadow-xl"
                style={{ width: '280px', fontFamily: "'Inter', sans-serif" }}
                onClick={e => e.stopPropagation()}
              >
                <div className="flex flex-col gap-1">
                  <div className="flex justify-between items-center">
                    <span className="text-white text-xs tracking-widest uppercase font-light">Size</span>
                    <span className="text-gray-300 text-xs">{size}%</span>
                  </div>
                  <input type="range" min={30} max={200} value={size} onChange={e => setSize(Number(e.target.value))} className="w-full accent-white cursor-pointer" />
                </div>
                <div className="flex flex-col gap-1">
                  <div className="flex justify-between items-center">
                    <span className="text-white text-xs tracking-widest uppercase font-light">Horizontal</span>
                    <span className="text-gray-300 text-xs">{offsetX}px</span>
                  </div>
                  <input type="range" min={-300} max={300} value={offsetX} onChange={e => setOffsetX(Number(e.target.value))} className="w-full accent-white cursor-pointer" />
                </div>
                <div className="flex flex-col gap-1">
                  <div className="flex justify-between items-center">
                    <span className="text-white text-xs tracking-widest uppercase font-light">Vertical</span>
                    <span className="text-gray-300 text-xs">{offsetY}px</span>
                  </div>
                  <input type="range" min={-300} max={300} value={offsetY} onChange={e => setOffsetY(Number(e.target.value))} className="w-full accent-white cursor-pointer" />
                </div>
                <button
                  type="button"
                  onClick={e => { e.stopPropagation(); setSize(100); setOffsetX(0); setOffsetY(0); }}
                  className="mt-1 text-xs text-gray-400 hover:text-white tracking-widest uppercase transition-colors duration-200 cursor-pointer text-center"
                >
                  Reset to Default
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ position: 'fixed', top: '-9999px', left: '-9999px', visibility: 'hidden' }}
        onChange={handleImageChange}
      />
      <input
        ref={audioFileInputRef}
        type="file"
        accept="audio/*"
        style={{ position: 'fixed', top: '-9999px', left: '-9999px', visibility: 'hidden' }}
        onChange={handleAudioUpload}
      />

      {/* Bottom-left audio controls */}
      <div className="absolute bottom-6 left-6 z-40 flex items-center gap-3" style={{ pointerEvents: 'auto' }}>
        {/* Mute / Unmute button — always visible when audio exists */}
        {audioUrl && (
          <button
            type="button"
            onClick={toggleMute}
            title={isMuted ? 'Unmute music' : 'Mute music'}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm border border-white/20 text-white hover:bg-black/70 transition-all duration-200 cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`text-base ${isMuted ? 'ri-volume-mute-line' : 'ri-volume-up-line'}`}></i>
            </div>
          </button>
        )}

        {/* Upload audio button — edit mode only */}
        {isEditMode && (
          <button
            type="button"
            onClick={() => audioFileInputRef.current?.click()}
            disabled={isUploadingAudio}
            title="Upload background music"
            className="flex items-center gap-2 px-3 py-2 rounded-full bg-black/50 backdrop-blur-sm border border-white/20 text-white text-xs tracking-widest uppercase font-light hover:bg-black/70 transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              {audioUploadStatus === 'uploading' ? (
                <span className="inline-block w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></span>
              ) : audioUploadStatus === 'done' ? (
                <i className="ri-check-line text-green-400 text-sm"></i>
              ) : audioUploadStatus === 'error' ? (
                <i className="ri-error-warning-line text-red-400 text-sm"></i>
              ) : (
                <i className="ri-music-2-line text-sm"></i>
              )}
            </div>
            {audioUploadStatus === 'uploading' ? 'Uploading...' : audioUploadStatus === 'done' ? 'Music Set!' : audioUploadStatus === 'error' ? 'Failed' : audioUrl ? 'Change Music' : 'Add Music'}
          </button>
        )}
      </div>

      {/* Entrance content */}
      <div className="relative z-30 flex flex-col items-center justify-end h-full pb-16 px-8" style={{ pointerEvents: 'none' }}>
        <div className="text-center mb-10">
          <h1 className="text-7xl font-light tracking-wide text-white mb-4" style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif", textShadow: '0 2px 24px rgba(0,0,0,0.5)' }}>
            JOSHUA SMITH
          </h1>
          <p className="text-xl font-light tracking-widest text-white/80 uppercase" style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif", textShadow: '0 1px 12px rgba(0,0,0,0.4)' }}>
            Contemporary Gallery
          </p>
        </div>

        <button
          onClick={handleEnter}
          disabled={isEntering}
          className="group relative px-12 py-4 bg-white/10 backdrop-blur-sm border border-white/40 text-white text-sm font-light tracking-widest uppercase transition-all duration-300 hover:bg-white/20 disabled:opacity-50 whitespace-nowrap cursor-pointer"
          style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif", pointerEvents: 'auto' }}
        >
          <span className={`transition-opacity duration-300 ${isEntering ? 'opacity-0' : 'opacity-100'}`}>
            Enter Gallery
          </span>
          {isEntering && (
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={onContact}
          className="mt-3 flex items-center gap-2 px-8 py-3 bg-transparent border border-white/30 text-white/80 text-xs font-light tracking-widest uppercase hover:bg-white/10 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
          style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif", pointerEvents: 'auto' }}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-mail-line text-sm"></i>
          </div>
          Contact
        </button>

        <p className="mt-6 text-xs font-light tracking-wide text-white/60 uppercase" style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}>
          Virtual Exhibition Space
        </p>

        <a
          href="mailto:joshuasmithart@gmail.com"
          className="mt-3 text-2xl font-light tracking-wide text-white/70 hover:text-white transition-colors duration-200 cursor-pointer"
          style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif", pointerEvents: 'auto' }}
        >
          joshuasmithart@gmail.com
        </a>
      </div>

      {isEntering && (
        <div className="absolute inset-0 z-40 bg-white animate-fadeIn" style={{ animationDuration: '800ms' }}></div>
      )}
    </div>
  );
}
